import { useMutation, useQuery, useQueryClient } from 'react-query';
import { predictionManagerAxiosApi } from 'src/Config/axiosApi';

/**
 * Per-group opener pod lifecycle — prediction-manager orchestrates a
 * dedicated engine pod + Socket.io feed pod per pair group. These hooks
 * cover the group-scoped start/stop/panic controls and the pod-state poll
 * that feeds the status chips + the WS feed-target resolution.
 *
 * Wire contract (GET /api/system/opener/groups/state) is fail-soft: when
 * the cluster is unreachable the k8s-derived fields (`engineReplicas`,
 * `engineReady`, `feedReady`, `feedPath`) may be omitted — consumers must
 * treat a missing field as NOT ready.
 */
export interface OpenerGroupPodState {
  groupId: string;
  name: string;
  podsDesired: 'on' | 'off' | 'panicked';
  engineReplicas?: number;
  engineReady?: boolean;
  feedReady?: boolean;
  feedPath?: string;
}

export interface OpenerGroupsPodStateResponse {
  groups: OpenerGroupPodState[];
}

export const OPENER_GROUP_POD_STATE_KEY = ['opener-group-pod-state'];

export async function fetchOpenerGroupsPodState(): Promise<
  OpenerGroupPodState[]
> {
  const { data } =
    await predictionManagerAxiosApi.get<OpenerGroupsPodStateResponse>(
      '/api/system/opener/groups/state'
    );
  return Array.isArray(data?.groups) ? data.groups : [];
}

/**
 * Poll the per-group pod state every 5 s. react-query's default
 * `refetchIntervalInBackground: false` pauses the poll while the tab is
 * not focused, so this only ticks while the operator is looking.
 */
export function useOpenerGroupsPodState(opts?: { enabled?: boolean }) {
  return useQuery<OpenerGroupPodState[]>(
    OPENER_GROUP_POD_STATE_KEY,
    fetchOpenerGroupsPodState,
    {
      enabled: opts?.enabled ?? true,
      refetchInterval: 5_000,
      keepPreviousData: true
    }
  );
}

/* ------------------------------------------------------------------ */
/* Request functions (exported for wire-shape tests)                   */
/* ------------------------------------------------------------------ */

// Start can be refused with a 409/400 (global panic lockout armed, Kalshi
// key-slot pool exhausted) — the server message is surfaced by the global
// MutationCache.onError toast via `extractServerErrorMessage`.
export async function startOpenerGroup(groupId: string) {
  const { data } = await predictionManagerAxiosApi.post(
    `/api/system/opener/groups/${groupId}/start`
  );
  return data;
}

export async function stopOpenerGroup(groupId: string) {
  const { data } = await predictionManagerAxiosApi.post(
    `/api/system/opener/groups/${groupId}/stop`
  );
  return data;
}

export async function panicSoftOpenerGroup(groupId: string) {
  const { data } = await predictionManagerAxiosApi.post(
    `/api/system/opener/groups/${groupId}/panic-soft`
  );
  return data;
}

export async function panicOpenerGroup(groupId: string) {
  const { data } = await predictionManagerAxiosApi.post(
    `/api/system/opener/groups/${groupId}/panic`
  );
  return data;
}

/* ------------------------------------------------------------------ */
/* Mutations                                                           */
/* ------------------------------------------------------------------ */

export function useOpenerGroupStart() {
  const qc = useQueryClient();
  return useMutation(startOpenerGroup, {
    mutationKey: 'entry:group-start',
    onSuccess: () => {
      qc.invalidateQueries(OPENER_GROUP_POD_STATE_KEY);
    }
  });
}

export function useOpenerGroupStop() {
  const qc = useQueryClient();
  return useMutation(stopOpenerGroup, {
    mutationKey: 'entry:group-stop',
    onSuccess: () => {
      qc.invalidateQueries(OPENER_GROUP_POD_STATE_KEY);
      // Stop pauses the group's rules server-side.
      qc.invalidateQueries('entry-rules');
    }
  });
}

/** Soft panic — cancel + pause the group's rules; engine pod stays up. */
export function useOpenerGroupPanicSoft() {
  const qc = useQueryClient();
  return useMutation(panicSoftOpenerGroup, {
    mutationKey: 'entry:group-panic-soft',
    onSuccess: () => {
      qc.invalidateQueries(OPENER_GROUP_POD_STATE_KEY);
      qc.invalidateQueries('entry-rules');
    }
  });
}

/**
 * Hard panic — kill the group engine pod + cancel ALL the group's orders
 * externally from prediction-manager. No lockout: the group can be
 * restarted immediately.
 */
export function useOpenerGroupPanic() {
  const qc = useQueryClient();
  return useMutation(panicOpenerGroup, {
    mutationKey: 'entry:group-panic',
    onSuccess: () => {
      qc.invalidateQueries(OPENER_GROUP_POD_STATE_KEY);
      qc.invalidateQueries('entry-rules');
    }
  });
}
